import React, {useContext, useEffect, useState} from "react";
import type {ReactNode} from "react";
import {useAuth} from "./AuthContext";
import {getRegularCompany} from "../services/CompanyService";
import {getCompanies} from "../services/system/CompanyService";
import type {Company} from "../api/generated";

interface CompaniesData {
  loading: boolean;
  companies: Company[];
}

export interface CompanyContextValue {
  changeCompany: () => void;
  getCompaniesList: () => Company[];
  company: Company | null;
  updateCompanies: () => void;
}

const CompanyContext = React.createContext<CompanyContextValue>(undefined as unknown as CompanyContextValue);

export function useCompany(): CompanyContextValue {
  return useContext(CompanyContext);
}

export function CompanyProvider({children}: {children?: ReactNode}) {
  const {isSuperAdmin, isCompanyGroupAdmin, currentUser} = useAuth();
  const [companiesData, setCompaniesData] = useState<CompaniesData>({
    loading: true,
    companies: []
  });

  const [reload, setReload] = useState(false);
  // `company` = the CALLER'S OWN company. For a company admin/trainer it's their assigned company.
  // For a super-admin it is ALWAYS null: a super-admin has no company context and never "becomes" a
  // company (the old drill-in was removed — company-scoped work happens via impersonation, future).
  const [company, setCompany] = useState<Company | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    // A COMPANY_GROUP_ADMIN is company-less like the super-admin, so it takes the same `company: null`
    // path — but WITHOUT the super-admin's global company list, which is SYSTEM_ADMIN-only. Falling
    // into the else branch below meant calling getRegularCompany(undefined): the generated client
    // drops a nullish path param (core/utils.gen.ts) and shipped the placeholder literally, as
    // GET /admin/company/company/%7Bid%7D — a malformed URL on a floor this persona isn't on.
    if (isCompanyGroupAdmin()) {
      setCompany(null);
      setCompaniesData({loading: false, companies: []});
    } else if (isSuperAdmin()) {
      // Super-admin: load the companies list only for global pickers (import-users, coupons,
      // notifications, menu templates via getCompaniesList). No selected company — `company` stays null.
      setCompany(null);
      getCompanies()
        .then(res => setCompaniesData({loading: false, companies: res.data || []}))
        .catch(() => setCompaniesData({loading: false, companies: []}));
    } else {
      setCompaniesData({
        loading: false,
        companies: []
      });

      // For regular users, get their assigned company.
      // The catch matters: without it a failed load leaves `company` null forever and every
      // company-gated page (notifications, food, clients) silently renders an endless spinner
      // instead of failing visibly. Keep the company null on error, but don't swallow the reason.
      getRegularCompany(currentUser.companyId)
        .then(res => {
          setCompany(res.data ?? null);
        })
        .catch(e => {
          console.error("Failed to load the caller's company", e);
          setCompany(null);
        });
    }
    // Re-runs when the authenticated identity changes — critically, when a super-admin
    // impersonates a company admin (currentUser.id flips to the target) we drop the SA's
    // null company and load the impersonated company's, so Settings/branding render. Same on
    // stop-impersonation (identity flips back → company reset to null for the SA).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, currentUser?.id]);

  const updateCompanies = () => {
    setReload(prv => !prv);
  }

  // Drill-in removed. Kept as a no-op so existing optional-chained callers (e.g. the company
  // breakdown table's row click) don't break; a super-admin can no longer scope into a company.
  const changeCompany = () => {};

  const getCompaniesList = () => {
    return companiesData.companies;
  }

  const value: CompanyContextValue = {
    changeCompany,
    getCompaniesList,
    company,
    updateCompanies,
  };

  return (
      <CompanyContext.Provider value={value}>
        {!companiesData.loading && children}
      </CompanyContext.Provider>
  );
};
